import { AnimationAction } from "three";
import Animation from "./animation";
import { ScenarioOption } from "./type";

export default class AnimationScenario<ScenarioKeys extends string> extends Animation {

  options: ScenarioOption
  delta: number;
  isBlending: boolean;
  isScenarioPlaying: boolean;
  index: number;
  private isScenarioEnded: boolean;
  private isEntryClip: boolean;

  scenarios: Record<ScenarioKeys, string[]> = {} as Record<ScenarioKeys, string[]>;
  currScenario: string[];
  public onEnd: ((...args: unknown[]) => void) | null = null


  constructor(animations: { [name: string]: AnimationAction }) {
    super(animations);

    this.options = { duration: 0.2, isLastAnimationLoop: false, isRandom: false, blendDelta: 10 }
    this.isBlending = false;
    this.isScenarioPlaying = false;
    this.index = 0;
    this.isScenarioEnded = false;
    this.isEntryClip = false;
    this.scenarios = {} as Record<ScenarioKeys, string[]>;
    this.currScenario = [];
    this.delta = 1 / 30;
    // this.delta = this.root.tick.delta * 0.001;
  }

  playScenarioByName(name: ScenarioKeys, options?: Partial<ScenarioOption>): void {
    if (!(name in this.scenarios)) {
      console.error(`❌ Scenario not found: ${name}. Available scenarios:`, Object.keys(this.scenarios));
      return;
    }


    // 시나리오가 비어있는지 확인
    if (!this.scenarios[name] || this.scenarios[name].length === 0) {
      console.error(`❌ Scenario "${name}" is empty or undefined`);
      return;
    }


    this.isScenarioPlaying = true;
    this.index = 0;
    this.isScenarioEnded = false; // 새 시나리오 시작 시 플래그 초기화
    this.isEntryClip = true;
    // onEnd 콜백 초기화 제거 - nextScenario 기능을 위해 BaseAvatarPlayer에서 설정한 콜백을 보존
    // if (this.onEnd) this.onEnd = null;


    const defaultOptions = { duration: 0.2, isLastAnimationLoop: false, isRandom: false, blendDelta: 10 }
    this.options = { ...defaultOptions, ...options, isLastAnimationLoop: this.scenarios[name].length === 1 || !!options?.isLastAnimationLoop }

    this.currScenario = this.options.isRandom ? this.scenarios[name].toSorted(() => Math.random() - 0.5) : [...this.scenarios[name]];

    // 진입 첫 클립을 지정 목록(액션)에서 보장 — 아이들 진입 직후 바로 눈에 띄는 동작이 나오도록
    const entryClips = this.options.entryClips;
    if (entryClips && entryClips.length > 0 && this.currScenario.length > 1) {
      const candidates = this.currScenario
        .map((clipName, i) => ({ clipName, i }))
        .filter(c => entryClips.includes(c.clipName));
      if (candidates.length > 0) {
        const pick = candidates[Math.floor(Math.random() * candidates.length)].i;
        if (pick !== 0) {
          [this.currScenario[0], this.currScenario[pick]] = [this.currScenario[pick], this.currScenario[0]];
        }
        console.log(`🎭 [Scenario] 진입 클립 보장: ${this.currScenario[0]}`);
      }
    }

    this.play(this.currScenario[this.index++], { duration: this.options.duration, loop: true });
  }

  stopScenario(): void {
    this.isScenarioPlaying = false;
    this.index = 0;
    this.isScenarioEnded = false; // 시나리오 중지 시 플래그 초기화
    this.isEntryClip = false;
    this.onEnd = null;
    this.stop();
  }

  //시나리오의 애니메이션 리스트를 수정합니다.
  modifyScenario(name: ScenarioKeys, scenario: string[]): this | null {
    if (!(name in this.scenarios)) return this
    this.scenarios[name] = scenario;
    return this
  }

  getAnimationInfo() {
    const clip = this.newAction?.getClip() ?? null;
    const time = this.newAction?.time ?? 0;
    const duration = clip?.duration ?? 0;
    // const timeToChange = clip?.duration - this.deltaTime * this.options.blendDeltaTime
    // const isTimeToBlend = currentTime >= timeToChange && !this.isBlending;
    const scenarioLength = this.currScenario.length;
    const blendDelta = this.delta * (this.options.blendDelta ?? 10);
    // maxClipSec: 긴 클립이 끝나기를 기다리지 않고 일정 시간 후 다음 클립으로 전환 (다중 클립 시나리오 한정)
    const maxClipSec = this.isEntryClip
      ? (this.options.entryMaxClipSec ?? this.options.maxClipSec)
      : this.options.maxClipSec;
    const overMaxClip = maxClipSec != null && scenarioLength > 1 && time >= maxClipSec;
    const shouldBlend = (time >= (duration - blendDelta) || overMaxClip) && !this.isBlending
    return { clip, time, duration, scenarioLength, blendDelta, shouldBlend }
  }

  private shuffleScenario(): void {
    const lastAnimation = this.currScenario[this.currScenario.length - 1];
    this.currScenario = [...this.currScenario].sort(() => Math.random() - 0.5);

    // 마지막 애니메이션이 첫 번째로 오지 않도록 조정
    if (this.currScenario[0] === lastAnimation) {
      [this.currScenario[0], this.currScenario[this.currScenario.length - 1]] =
        [this.currScenario[this.currScenario.length - 1], this.currScenario[0]];
    }
  }

  blendToNextAnimation(): void {
    this.isBlending = true;
    this.isEntryClip = false;
    this.play(this.currScenario[this.index], { duration: this.options.duration, loop: true });
    this.index++;
    this.isBlending = false;
  }

  handleScenarioEnd(): void {
    // onEnd 콜백은 시나리오당 1회만 실행 (중복 실행 방지)
    // 주의: 종전에는 여기서 전체를 early return 해서, 2번째 사이클 종료 후
    // index 리셋·셔플이 멈추고 마지막 클립만 무한 루프되는 퇴화가 있었음 (random_loop 다양성 소실)
    if (!this.isScenarioEnded) {
      this.isScenarioEnded = true;

      if (this.onEnd) {
        this.onEnd();
        this.onEnd = null;
        this.index = 0;
        // onEnd 콜백 초기화를 제거 - nextScenario 기능을 위해 콜백을 보존
        // 각 시나리오마다 BaseAvatarPlayer에서 새로운 onEnd를 설정하므로 여기서 초기화할 필요 없음
      }
    }

    //마지막 애니메이션이 루핑되지 않고, 최초 시나리오로 돌아가도록
    if (!this.options.isLastAnimationLoop) {
      this.index = 0;
    }

    //랜덤 애니메이션인 경우 셔플되도록 조정
    if (this.options.isRandom) {
      this.shuffleScenario();
    }
  }

  update(): void {
    if (!this.isScenarioPlaying) return

    //IDLE 상태에서는 애니메이션 시나리오 재생 중지
    //PLAYING 상태에서는 애니메이션 시나리오 재생
    //만약, PLAYING이 BLENDING으로 바뀌면? update 함수는 사용할 수 없음. play하는 직후 State를 BLENDING => PLAYING으로 변경해야함

    const { scenarioLength, shouldBlend } = this.getAnimationInfo.bind(this)();
    const isIndexInScenario = this.index < scenarioLength;
    if (!shouldBlend) return

    if (isIndexInScenario) this.blendToNextAnimation();
    else this.handleScenarioEnd();
  }
}
